import { Button, LoadingSpinner, Text } from "@/components/ui";
import { type DefaultMatterFileRepositoryStructureFolder } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { Folder } from "lucide-react";

function renderFolderReadOnly(
  folders: DefaultMatterFileRepositoryStructureFolder[],
  folder: DefaultMatterFileRepositoryStructureFolder,
  level: number,
  index: number,
) {
  const children = folders.filter((f) => f.idLocation === folder.id);

  return (
    <div key={folder.id}>
      <div
        className={cn(
          "flex cursor-default items-center rounded py-1 pr-1 pl-2",
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        role="treeitem"
        aria-level={level + 1}
        aria-posinset={index + 1}
      >
        <Folder className="mr-2 h-5 w-5 shrink-0 text-amber-500" aria-hidden />
        <span className="text-foreground flex-grow truncate text-sm">
          {folder.name}
        </span>
      </div>
      {children.length > 0 ? (
        <div role="group">
          {children.map((child, childIndex) =>
            renderFolderReadOnly(folders, child, level + 1, childIndex),
          )}
        </div>
      ) : null}
    </div>
  );
}

interface FolderStructurePreviewProps {
  folders: DefaultMatterFileRepositoryStructureFolder[];
  isLoading: boolean;
  onEdit?: () => void;
}

export function FolderStructurePreview({
  folders,
  isLoading,
  onEdit,
}: FolderStructurePreviewProps) {
  if (isLoading) {
    return (
      <div className="text-muted-foreground flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }

  if (folders.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <Text variant="subHeading">No folders in this structure yet.</Text>
        {onEdit ? <Button onClick={onEdit}>Edit structure</Button> : null}
      </div>
    );
  }

  const rootFolders = folders.filter(
    (folder) => folder.idLocation === "" || folder.idLocation === null,
  );

  return (
    <div>
      <Text
        variant="caption"
        className="mb-3 font-semibold tracking-wide uppercase"
      >
        Folder structure preview
      </Text>
      <div
        className="bg-muted/30 rounded-lg border p-3"
        role="tree"
        aria-label="Folder structure"
      >
        {rootFolders.map((folder, index) =>
          renderFolderReadOnly(folders, folder, 0, index),
        )}
      </div>
    </div>
  );
}
